'use client';
import React from 'react';
import {
  AlertTriangle,
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Eye,
  Navigation,
  Sun,
  TriangleAlert,
  Wind,
} from 'lucide-react';
import { WeatherData, FlightCategory } from '@/lib/types';
import { cyrCRunways } from '@/lib/runwayData';
import { selectRecommendedRunway } from '@/lib/runwayWindCalculator';
import {
  getWeatherIconForConditions,
  WeatherIconDescriptor,
  WeatherConditions,
} from '@/lib/weatherIconMapper';

interface ForecastTimelineProps {
  weatherData: WeatherData;
}

interface ForecastPoint {
  key: string;
  label: string;
  localTime: string;
  category: FlightCategory;
  windDirection: number;
  windSpeed: number;
  gustSpeed: number;
  visibility: string;
  ceiling: string;
  ceilingFt: number;
  weather: string;
  runway: string;
  source: 'METAR' | 'TAF' | 'EST';
  alerts: string[];
  // Only set on the "Actuel" card
  temperature?: number;
  dewpoint?: number;
  pressure?: number;
  humidity?: number;
  isVfrWindow: boolean;
  // TEMPO / PROB overlay
  tempoActive?: boolean;
  probActive?: boolean;
  probPercent?: number;
  tempoWindDirection?: number;
  tempoWindSpeed?: number;
  tempoGustSpeed?: number;
  tempoVisibility?: string;
  tempoCeilingFt?: number;
  tempoCeilingLayer?: string;
  tempoWeather?: string;
  // Journée summary
  isDay?: boolean;
  worstCategory?: FlightCategory;
  windRange?: string;
  minCeilingFt?: number;
  minVisibility?: string;
  phenomena?: string[];
  hasTempoOrProb?: boolean;
}

interface TafSegment {
  type: 'BASE' | 'FM' | 'TEMPO' | 'BECMG' | 'PROB';
  raw: string;
  fromZulu?: number; // minutes since midnight UTC
  toZulu?: number;
  windDirection?: number;
  windSpeed?: number;
  gustSpeed?: number;
  visibility?: string;
  ceilingFt?: number;
  ceilingLayer?: string;
  weather?: string;
  probPercent?: number;
}

// ─── Time helpers ────────────────────────────────────────────────────────────

/** Returns the current Zulu (UTC) time as minutes since midnight UTC */
function nowZuluMinutes(): number {
  const d = new Date();
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/** Converts a local Date to Zulu minutes-since-midnight */
function dateToZuluMinutes(d: Date): number {
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/** Returns a Date object for "now + offsetHours" in local time */
function localDateForOffset(offsetHours: number): Date {
  return new Date(Date.now() + offsetHours * 60 * 60 * 1000);
}

/** Formats a Date as "HH h" in America/Toronto */
function formatLocalHour(d: Date): string {
  return d.toLocaleTimeString('fr-CA', {
    timeZone: 'America/Toronto',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function timeToMinutes(value?: string): number | null {
  if (!value || !/^\d{2}:\d{2}/.test(value)) return null;
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

function isInsideVfrWindow(localTime: string, weatherData: WeatherData): boolean {
  const start = timeToMinutes((weatherData.supplemental as any).vfrRecommendedStart);
  const end = timeToMinutes((weatherData.supplemental as any).vfrRecommendedEnd);
  const current = timeToMinutes(localTime);
  if (start === null || end === null || current === null) return true;
  return current >= start && current <= end;
}

// ─── TAF parsing ─────────────────────────────────────────────────────────────

function parseWind(raw: string): { windDirection?: number; windSpeed?: number; gustSpeed?: number } {
  const match = raw.match(/\b(\d{3}|VRB)(\d{2,3})(G(\d{2,3}))?KT\b/);
  if (!match) return {};
  return {
    windDirection: match[1] === 'VRB' ? 0 : Number(match[1]),
    windSpeed: Number(match[2]),
    gustSpeed: match[4] ? Number(match[4]) : 0,
  };
}

function parseVisibility(raw: string): string | undefined {
  const sm = raw.match(/\b(P?\d{1,2}(?:\/\d)?|\d\s+\d\/\d|P6)SM\b/);
  if (sm) return sm[0].replace('P6SM', '>6 SM');
  const metres = raw.match(/\b(\d{4})\b/);
  if (metres) return metres[1] === '9999' ? '>10 km' : `${metres[1]} m`;
  return undefined;
}

function parseCeiling(raw: string): { ceilingFt?: number; ceilingLayer?: string } {
  const ceilingMatch = raw.match(/\b(BKN|OVC|VV)(\d{3})\b/);
  if (ceilingMatch) {
    return { ceilingLayer: ceilingMatch[1], ceilingFt: Number(ceilingMatch[2]) * 100 };
  }
  const scatteredMatch = raw.match(/\b(SCT|FEW)(\d{3})\b/);
  if (scatteredMatch) {
    return { ceilingLayer: scatteredMatch[1], ceilingFt: Number(scatteredMatch[2]) * 100 };
  }
  if (/\b(SKC|CLR|NSC|CAVOK)\b/.test(raw)) {
    return { ceilingLayer: 'CLR', ceilingFt: 99999 };
  }
  return {};
}

function parseWeatherCode(raw: string): string | undefined {
  // Match all weather phenomenon tokens including intensity prefixes and compound codes
  // Covers: -SHRA, +SHRA, TSRA, VCTS, FZRA, BR, FG, etc.
  const tokens = raw.match(/\b[-+]?(VC)?(MI|PR|BC|DR|BL|SH|TS|FZ)?(DZ|RA|SN|SG|IC|PL|GR|GS|UP|BR|FG|FU|VA|DU|SA|HZ|PO|SQ|FC|SS|DS|TS)\b/g);
  if (!tokens || tokens.length === 0) return undefined;
  // Also check for CB/TCU in cloud groups — extract as separate phenomena
  const cbMatch = raw.match(/\b(FEW|SCT|BKN|OVC|VV)\d{3}(CB|TCU)\b/g);
  const cbTokens = cbMatch ? cbMatch.map(m => m.match(/(CB|TCU)$/)?.[1] ?? '') : [];
  const all = [...tokens, ...cbTokens].filter(Boolean);
  return all.length > 0 ? all.join(' ') : undefined;
}

/**
 * Parse a DDHH validity group from a TAF line.
 * Returns { fromZulu, toZulu } in minutes-since-midnight UTC.
 * For FM lines: FMDDHHMM → fromZulu only.
 */
function parseTafValidity(line: string, type: TafSegment['type']): { fromZulu?: number; toZulu?: number } {
  if (type === 'FM') {
    // FM161830 → day=16, hour=18, min=30
    const m = line.match(/^FM\d{2}(\d{2})(\d{2})/);
    if (m) return { fromZulu: Number(m[1]) * 60 + Number(m[2]) };
    return {};
  }
  // TEMPO/BECMG/PROB: DDHH/DDHH
  const m = line.match(/\b\d{2}(\d{2})\/\d{2}(\d{2})\b/);
  if (m) return { fromZulu: Number(m[1]) * 60, toZulu: Number(m[2]) * 60 };
  return {};
}

function parseTafSegments(rawTaf: string): TafSegment[] {
  if (!rawTaf) return [];
  const normalized = rawTaf
    .replace(/\s+(FM\d{6}|TEMPO|BECMG|PROB\d{2})/g, '\n$1')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return normalized.map((line, index) => {
    const segType: TafSegment['type'] =
      line.startsWith('FM')    ? 'FM'    :
      line.startsWith('TEMPO') ? 'TEMPO' :
      line.startsWith('BECMG') ? 'BECMG' :
      line.startsWith('PROB')  ? 'PROB'  : 'BASE';

    const type = index === 0 ? 'BASE' : segType;
    const wind    = parseWind(line);
    const ceiling = parseCeiling(line);
    const validity = parseTafValidity(line, type);

    // Extract PROB percentage
    let probPercent: number | undefined;
    if (type === 'PROB') {
      const pm = line.match(/^PROB(\d{2})/);
      if (pm) probPercent = Number(pm[1]);
    }

    return {
      type,
      raw: line,
      ...validity,
      ...wind,
      ...ceiling,
      visibility: parseVisibility(line),
      weather: parseWeatherCode(line),
      probPercent,
    };
  });
}

/**
 * Given a Zulu time in minutes-since-midnight, find the active base/FM/BECMG segment
 * and any overlapping TEMPO/PROB segments.
 * Handles day rollover: TEMPO/PROB with toZulu < fromZulu spans midnight.
 */
function getSegmentsAtZulu(
  zuluMinutes: number,
  segments: TafSegment[]
): { base: TafSegment | null; tempo: TafSegment | null; prob: TafSegment | null } {
  if (segments.length === 0) return { base: null, tempo: null, prob: null };

  // Base segment: last BASE or FM whose fromZulu <= zuluMinutes
  let base: TafSegment | null = null;
  for (const seg of segments) {
    if (seg.type === 'BASE') {
      base = seg;
    } else if (seg.type === 'FM' || seg.type === 'BECMG') {
      if (seg.fromZulu !== undefined && seg.fromZulu <= zuluMinutes) {
        base = seg;
      }
    }
  }
  // Fallback to first segment
  if (!base) base = segments[0];

  // Helper: check if zuluMinutes falls within [from, to), handling midnight rollover
  function inWindow(from: number, to: number, zulu: number): boolean {
    // toZulu of 0 means midnight next day → treat as 1440
    const toNorm = to === 0 ? 1440 : to;
    if (toNorm > from) {
      // Normal window (e.g. 18:00 → 23:00)
      return zulu >= from && zulu < toNorm;
    } else {
      // Spans midnight (e.g. 22:00 → 02:00): active if zulu >= from OR zulu < to
      return zulu >= from || zulu < toNorm;
    }
  }

  // TEMPO: find one whose validity window contains zuluMinutes
  let tempo: TafSegment | null = null;
  for (const seg of segments) {
    if (seg.type === 'TEMPO') {
      const from = seg.fromZulu ?? 0;
      const to = seg.toZulu ?? 1440;
      if (inWindow(from, to, zuluMinutes)) {
        tempo = seg;
        break;
      }
    }
  }

  // PROB: find one whose validity window contains zuluMinutes
  let prob: TafSegment | null = null;
  for (const seg of segments) {
    if (seg.type === 'PROB') {
      const from = seg.fromZulu ?? 0;
      const to = seg.toZulu ?? 1440;
      if (inWindow(from, to, zuluMinutes)) {
        prob = seg;
        break;
      }
    }
  }

  return { base, tempo, prob };
}

// ─── Decoding helpers ─────────────────────────────────────────────────────────

function decodeCloudLayer(layer: string, ft: number): string {
  if (layer === 'CLR' || ft >= 99999) return 'Dégagé';
  const coverage: Record<string, string> = {
    FEW: 'Quelques nuages',
    SCT: 'Épars',
    BKN: 'Fragmenté',
    OVC: 'Couvert',
    VV:  'Plafond vertical',
  };
  const label = coverage[layer] ?? layer;
  return `${label} ${ft} ft`;
}

function decodeWeather(code: string | undefined): string {
  if (!code) return 'RAS';
  const map: Record<string, string> = {
    'TSRA': 'Orage avec pluie',
    'TS':   'Orage',
    '+TSRA':'Fort orage avec pluie',
    'SHRA': 'Averses de pluie',
    '-SHRA':'Averses de pluie faibles',
    '+SHRA':'Fortes averses de pluie',
    'RA':   'Pluie',
    '-RA':  'Pluie faible',
    '+RA':  'Pluie forte',
    'DZ':   'Bruine',
    '-DZ':  'Bruine faible',
    'FZRA': 'Pluie verglaçante',
    'FZDZ': 'Bruine verglaçante',
    'SN':   'Neige',
    '-SN':  'Neige faible',
    '+SN':  'Neige forte',
    'SG':   'Grésil',
    'GR':   'Grêle',
    'GS':   'Petite grêle',
    'FG':   'Brouillard',
    'BCFG': 'Bancs de brouillard',
    'MIFG': 'Brouillard bas',
    'FZFG': 'Brouillard givrant',
    'BR':   'Brume',
    'HZ':   'Brume sèche',
    'FU':   'Fumée',
    'SA':   'Sable',
    'DU':   'Poussière',
    'BLSN': 'Poudrerie haute',
    'DRSN': 'Poudrerie basse',
    'PL':   'Grésil',
    'UP':   'Précipitations inconnues',
    'VCSH': 'Averses à proximité',
    'VCTS': 'Orage à proximité',
    'CB':   'Cumulonimbus',
    'TCU':  'Cumulus bourgeonnant',
  };
  // Try exact match first
  if (map[code]) return map[code];
  // Try token by token
  const tokens = code.split(/\s+/);
  const decoded = tokens.map((t) => map[t] ?? t);
  return decoded.join(', ');
}

/** Detect if a weather code string contains dangerous phenomena */
function hasDangerousPhenomena(weather: string | undefined): boolean {
  if (!weather) return false;
  return /\b(TS|TSRA|CB|VCTS|FZRA|FZDZ|GR)\b/.test(weather);
}

function hasSignificantPhenomena(weather: string | undefined): boolean {
  if (!weather) return false;
  return /\b(TS|TSRA|CB|VCTS|FZRA|FZDZ|GR|SHRA|RA|BR|FG|SN|SHSN|BLSN)\b/.test(weather);
}

function decodeVisibility(vis: string | undefined): string {
  if (!vis) return '--';
  if (vis.startsWith('>6 SM') || vis.startsWith('>6SM')) return 'Plus de 6 SM';
  if (vis.startsWith('>10 km')) return 'Plus de 10 km';
  return vis;
}

// ─── Flight category ──────────────────────────────────────────────────────────

function deriveFlightCategory(visibility: string, ceilingFt: number): FlightCategory {
  const smMatch = visibility.match(/^(\d+(?:\.\d+)?)\s*SM/);
  const kmMatch = visibility.match(/^(\d+(?:\.\d+)?)\s*km/);
  const mMatch  = visibility.match(/^(\d+)\s*m/);

  let visSm = 6;
  if (visibility.startsWith('>')) visSm = 7;
  else if (smMatch) visSm = Number(smMatch[1]);
  else if (kmMatch) visSm = Number(kmMatch[1]) * 0.621371;
  else if (mMatch)  visSm = Number(mMatch[1]) / 1609.34;

  if (ceilingFt < 500  || visSm < 1) return 'LIFR';
  if (ceilingFt < 1000 || visSm < 3) return 'IFR';
  if (ceilingFt < 3000 || visSm < 5) return 'MVFR';
  return 'VFR';
}

function worstCategory(a: FlightCategory, b: FlightCategory): FlightCategory {
  const rank: Record<FlightCategory, number> = { VFR: 0, MVFR: 1, IFR: 2, LIFR: 3 };
  return rank[a] >= rank[b] ? a : b;
}

// ─── Visual helpers ───────────────────────────────────────────────────────────

function WeatherIconComponent({ descriptor, size = 'lg' }: { descriptor: WeatherIconDescriptor; size?: 'lg' | 'sm' }) {
  const cls = size === 'lg' ? 'w-10 h-10' : 'w-7 h-7';
  const { iconName, colorClass } = descriptor;

  if (iconName === 'thunderstorm')  return <CloudLightning className={`${cls} ${colorClass}`} />;
  if (iconName === 'freezingRain')  return <TriangleAlert  className={`${cls} ${colorClass}`} />;
  if (iconName === 'snow')          return <CloudSnow      className={`${cls} ${colorClass}`} />;
  if (iconName === 'heavyRain')     return <CloudRain      className={`${cls} ${colorClass}`} />;
  if (iconName === 'showers')       return <CloudRain      className={`${cls} ${colorClass}`} />;
  if (iconName === 'lightRain')     return <CloudDrizzle   className={`${cls} ${colorClass}`} />;
  if (iconName === 'drizzle')       return <CloudDrizzle   className={`${cls} ${colorClass}`} />;
  if (iconName === 'fog')           return <CloudFog       className={`${cls} ${colorClass}`} />;
  if (iconName === 'mist')          return <CloudFog       className={`${cls} ${colorClass}`} />;
  if (iconName === 'overcast')      return <Cloud          className={`${cls} ${colorClass}`} />;
  if (iconName === 'brokenClouds')  return <Cloud          className={`${cls} ${colorClass}`} />;
  if (iconName === 'partlyCloudy')  return <CloudSun       className={`${cls} ${colorClass}`} />;
  if (iconName === 'mostlySunny')   return <CloudSun       className={`${cls} ${colorClass}`} />;
  if (iconName === 'clear')         return <Sun            className={`${cls} ${colorClass}`} />;
  return <Eye className={`${cls} ${colorClass}`} />;
}

function cardThemeFromDescriptor(descriptor: WeatherIconDescriptor, tempoActive?: boolean): string {
  if (tempoActive)                                   return 'bg-amber-50 border-amber-400';
  const { iconName, severity } = descriptor;
  if (iconName === 'thunderstorm')                   return 'bg-purple-50 border-purple-300';
  if (iconName === 'freezingRain')                   return 'bg-red-50 border-red-300';
  if (iconName === 'snow')                           return 'bg-sky-50 border-sky-200';
  if (iconName === 'heavyRain' || iconName === 'showers') return 'bg-blue-50 border-blue-300';
  if (iconName === 'lightRain' || iconName === 'drizzle') return 'bg-blue-50 border-blue-200';
  if (iconName === 'fog' || iconName === 'mist')     return 'bg-slate-100 border-slate-300';
  if (iconName === 'overcast' || iconName === 'brokenClouds') return 'bg-slate-50 border-slate-200';
  if (severity === 'caution')                        return 'bg-amber-50 border-amber-300';
  return 'bg-white border-slate-100';
}

function categoryClass(category: FlightCategory): string {
  if (category === 'VFR')  return 'bg-green-100 text-green-700 border-green-300';
  if (category === 'MVFR') return 'bg-blue-100 text-blue-700 border-blue-300';
  if (category === 'IFR')  return 'bg-red-100 text-red-700 border-red-300';
  return 'bg-purple-100 text-purple-700 border-purple-300';
}

function buildAlerts(point: Omit<ForecastPoint, 'alerts'>): string[] {
  const alerts: string[] = [];

  // Check raw weather code for specific phenomena
  const rawWeather = point.weather ?? '';

  // Thunderstorm / CB
  if (/Orage|TSRA|orage/i.test(rawWeather)) alerts.push('⛈ Orage');
  else if (/Cumulonimbus|CB/i.test(rawWeather)) alerts.push('⚡ CB');

  // Freezing precipitation
  if (/verglaçant|FZRA|FZDZ/i.test(rawWeather)) alerts.push('🧊 Verglas');

  // Gusts
  if (point.gustSpeed >= 25) alerts.push(`💨 G${point.gustSpeed} kt`);
  else if (point.gustSpeed >= 20) alerts.push(`G${point.gustSpeed} kt`);

  // Low visibility
  if (point.ceilingFt < 3000 && point.ceilingFt < 99999) alerts.push(`Plaf. ${point.ceilingFt} ft`);

  // Mist / fog
  if (/Brume|Brouillard/i.test(rawWeather)) alerts.push('🌫 Brume/Brouillard');

  // Showers / rain
  if (/Averses|Pluie/i.test(rawWeather) && !/Orage/i.test(rawWeather)) alerts.push('🌧 Averses/Pluie');

  // TEMPO/PROB significant weather
  if (point.tempoActive) {
    const tempoWx = point.tempoWeather ?? '';
    if (/Orage|TSRA/i.test(tempoWx)) alerts.push('TEMPO ⛈');
    else if (/Averses|Pluie/i.test(tempoWx)) alerts.push('TEMPO 🌧');
    else if (/Brume|Brouillard/i.test(tempoWx)) alerts.push('TEMPO 🌫');
    else if (tempoWx && tempoWx !== 'RAS') alerts.push('TEMPO');
  }
  if (point.probActive) {
    const probWx = point.tempoWeather ?? '';
    const probLabel = point.probPercent ? `PROB${point.probPercent}` : 'PROB';
    if (/Orage|TSRA/i.test(probWx)) alerts.push(`${probLabel} ⛈`);
    else if (/Averses|Pluie/i.test(probWx)) alerts.push(`${probLabel} 🌧`);
    else if (/Brume|Brouillard/i.test(probWx)) alerts.push(`${probLabel} 🌫`);
    else alerts.push(probLabel);
  }

  return alerts;
}

// ─── Forecast stops ───────────────────────────────────────────────────────────

const FORECAST_OFFSETS = [0, 1, 2, 3, 6]; // hours from now (not including "day")

// ─── Build forecast ───────────────────────────────────────────────────────────

function buildForecast(weatherData: WeatherData): ForecastPoint[] {
  const { metar, taf } = weatherData;
  const segments = parseTafSegments(taf?.raw ?? '');

  // ── "Actuel" card — from METAR ──────────────────────────────────────────────
  const nowDate = localDateForOffset(0);
  const nowLocalTime = formatLocalHour(nowDate);
  const nowBase: Omit<ForecastPoint, 'alerts'> = {
    key: 'now',
    label: 'Actuel',
    localTime: nowLocalTime,
    category: metar.flightCategory,
    windDirection: metar.windDirection,
    windSpeed: metar.windSpeed,
    gustSpeed: metar.gustSpeed,
    visibility: decodeVisibility(`${metar.visibility} km`),
    ceiling: decodeCloudLayer(metar.ceilingLayer, metar.ceiling ?? 99999),
    ceilingFt: metar.ceiling ?? 99999,
    weather: decodeWeather(metar.weather) || 'RAS',
    runway: selectRecommendedRunway(metar.windDirection, metar.windSpeed, cyrCRunways),
    source: 'METAR',
    // Only the "Actuel" card carries METAR temperature/QNH/humidity
    temperature: metar.temperature,
    dewpoint: metar.dewpoint,
    pressure: metar.pressure,
    humidity: weatherData.supplemental.humidity,
    isVfrWindow: isInsideVfrWindow(nowLocalTime, weatherData),
  };
  const now: ForecastPoint = { ...nowBase, alerts: buildAlerts(nowBase) };

  const points: ForecastPoint[] = [now];

  // ── Future hourly cards — from TAF ─────────────────────────────────────────
  for (const offsetHours of FORECAST_OFFSETS.slice(1)) {
    const cardDate = localDateForOffset(offsetHours);
    const localTime = formatLocalHour(cardDate);
    const zuluMinutes = dateToZuluMinutes(cardDate);

    const { base, tempo, prob } = getSegmentsAtZulu(zuluMinutes, segments);

    // Base conditions (from TAF base/FM/BECMG)
    const windDirection = base?.windDirection ?? metar.windDirection;
    const windSpeed     = base?.windSpeed     ?? metar.windSpeed;
    const gustSpeed     = base?.gustSpeed     ?? 0;
    const ceilingFt     = base?.ceilingFt     ?? 99999;
    const ceilingLayer  = base?.ceilingLayer  ?? 'CLR';
    const visibility    = base?.visibility    ?? '>6 SM';
    const weatherCode   = base?.weather;

    const category = deriveFlightCategory(visibility, ceilingFt);
    const runway   = selectRecommendedRunway(windDirection, windSpeed, cyrCRunways);

    // TEMPO overlay
    const tempoActive = tempo !== null;
    const probActive  = prob !== null;

    // Decode TEMPO weather — use TEMPO segment's weather code
    const tempoWeatherDecoded = tempo?.weather ? decodeWeather(tempo.weather) : undefined;
    // For PROB, also decode its weather
    const probWeatherDecoded = prob?.weather ? decodeWeather(prob.weather) : undefined;

    const pointBase: Omit<ForecastPoint, 'alerts'> = {
      key: `h${offsetHours}`,
      label: localTime,
      localTime,
      category,
      windDirection,
      windSpeed,
      gustSpeed,
      visibility: decodeVisibility(visibility),
      ceiling: decodeCloudLayer(ceilingLayer, ceilingFt),
      ceilingFt,
      weather: decodeWeather(weatherCode),
      runway,
      source: segments.length > 0 ? 'TAF' : 'EST',
      // Future cards: NO temperature/QNH/humidity (not in TAF)
      isVfrWindow: isInsideVfrWindow(localTime, weatherData),
      tempoActive,
      probActive,
      probPercent: prob?.probPercent,
      // TEMPO overlay data
      tempoWindDirection: tempo?.windDirection,
      tempoWindSpeed:     tempo?.windSpeed,
      tempoGustSpeed:     tempo?.gustSpeed,
      tempoVisibility:    tempo?.visibility ? decodeVisibility(tempo.visibility) : undefined,
      tempoCeilingFt:     tempo?.ceilingFt,
      tempoCeilingLayer:  tempo?.ceilingLayer,
      tempoWeather:       tempoWeatherDecoded ?? probWeatherDecoded,
    };
    points.push({ ...pointBase, alerts: buildAlerts(pointBase) });
  }

  // ── "Journée" card — worst conditions across remaining VFR window ──────────
  const futurePoints = points.slice(1).filter((p) => p.isVfrWindow);
  let dayCategory: FlightCategory = 'VFR';
  let minCeilingFt = 99999;
  let minVisibility = '>6 SM';
  let minVisSm = 99;
  const phenomena: string[] = [];
  let hasTempoOrProb = false;
  const windDirs: number[] = [];
  const windSpeeds: number[] = [];

  for (const p of futurePoints) {
    dayCategory = worstCategory(dayCategory, p.category);
    if (p.ceilingFt < minCeilingFt) {
      minCeilingFt = p.ceilingFt;
      minVisibility = p.visibility;
    }
    // Track min visibility
    const smMatch = p.visibility.match(/(\d+(?:\.\d+)?)\s*SM/);
    const kmMatch = p.visibility.match(/(\d+(?:\.\d+)?)\s*km/);
    const mMatch  = p.visibility.match(/(\d+)\s*m/);
    let visSm = 99;
    if (p.visibility.startsWith('Plus de')) visSm = 7;
    else if (smMatch) visSm = Number(smMatch[1]);
    else if (kmMatch) visSm = Number(kmMatch[1]) * 0.621371;
    else if (mMatch)  visSm = Number(mMatch[1]) / 1609.34;
    if (visSm < minVisSm) {
      minVisSm = visSm;
      minVisibility = p.visibility;
    }
    if (p.weather && p.weather !== 'RAS' && !phenomena.includes(p.weather)) {
      phenomena.push(p.weather);
    }
    // Also collect TEMPO/PROB phenomena
    if (p.tempoWeather && p.tempoWeather !== 'RAS' && !phenomena.includes(p.tempoWeather)) {
      phenomena.push(p.tempoWeather);
    }
    if (p.tempoActive || p.probActive) hasTempoOrProb = true;
    windDirs.push(p.windDirection);
    windSpeeds.push(p.windSpeed);
  }

  const minSpeed = windSpeeds.length > 0 ? Math.min(...windSpeeds) : 0;
  const maxSpeed = windSpeeds.length > 0 ? Math.max(...windSpeeds) : 0;
  const minDir   = windDirs.length > 0 ? Math.min(...windDirs) : 0;
  const maxDir   = windDirs.length > 0 ? Math.max(...windDirs) : 0;
  const windRange = windSpeeds.length > 0
    ? (minDir === maxDir
        ? `${minDir}° / ${minSpeed}–${maxSpeed} kt`
        : `${minDir}°–${maxDir}° / ${minSpeed}–${maxSpeed} kt`)
    : '--';

  const dayLocalTime = formatLocalHour(localDateForOffset(9));
  const dayBase: Omit<ForecastPoint, 'alerts'> = {
    key: 'day',
    label: 'Journée',
    localTime: dayLocalTime,
    category: dayCategory,
    windDirection: windDirs.length > 0 ? Math.round(windDirs.reduce((a, b) => a + b, 0) / windDirs.length) : metar.windDirection,
    windSpeed: maxSpeed || metar.windSpeed,
    gustSpeed: 0,
    visibility: minVisibility || '>6 SM',
    ceiling: decodeCloudLayer(minCeilingFt < 99999 ? 'BKN' : 'CLR', minCeilingFt),
    ceilingFt: minCeilingFt,
    weather: phenomena.length > 0 ? phenomena[0] : 'RAS',
    runway: selectRecommendedRunway(
      windDirs.length > 0 ? Math.round(windDirs.reduce((a, b) => a + b, 0) / windDirs.length) : metar.windDirection,
      maxSpeed || metar.windSpeed,
      cyrCRunways
    ),
    source: segments.length > 0 ? 'TAF' : 'EST',
    isVfrWindow: true,
    isDay: true,
    worstCategory: dayCategory,
    windRange,
    minCeilingFt,
    minVisibility,
    phenomena,
    hasTempoOrProb,
  };
  points.push({ ...dayBase, alerts: buildAlerts(dayBase) });

  return points;
}

// ─── Card components ──────────────────────────────────────────────────────────

function ForecastCard({ point }: { point: ForecastPoint }) {
  // Out-of-VFR-window card
  if (!point.isVfrWindow) {
    return (
      <article className="rounded-2xl border border-slate-200 bg-slate-100 shadow-sm p-4 flex flex-col gap-3 min-h-[210px]">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-lg font-bold text-slate-700 font-tabular leading-none">
              {point.label}
            </div>
            <div className="text-xs text-slate-400 font-semibold mt-1">heure locale</div>
          </div>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-slate-200 text-slate-600 border-slate-300">
            HORS VFR
          </span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
          <Cloud className="w-14 h-14 text-slate-500" />
          <div>
            <div className="text-base font-bold text-slate-700">Météo VFR non disponible</div>
            <div className="text-xs text-slate-500 mt-1">Hors fenêtre de vol VFR recommandée</div>
          </div>
        </div>
        <div className="text-xs text-slate-400 font-semibold">
          Consultez les règles applicables pour les opérations de nuit.
        </div>
      </article>
    );
  }

  // Journée summary card
  if (point.isDay) {
    const conditions: WeatherConditions = {
      weatherDecoded: point.weather,
      ceilingLayer: point.ceilingFt !== undefined && point.ceilingFt < 99999 ? 'BKN' : 'CLR',
      ceilingFt: point.ceilingFt,
      visibility: point.visibility,
      category: point.worstCategory ?? point.category,
    };
    const descriptor = getWeatherIconForConditions(conditions);
    return (
      <article className={`rounded-2xl border shadow-sm p-3 flex flex-col gap-2.5 ${cardThemeFromDescriptor(descriptor)}`}>
        <div className="flex items-start justify-between gap-1">
          <div>
            <div className="text-base font-bold text-slate-800 leading-none">Journée</div>
            <div className="text-xs text-slate-400 mt-0.5">fenêtre VFR restante</div>
          </div>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${categoryClass(point.worstCategory ?? point.category)}`}>
            {point.worstCategory ?? point.category}
          </span>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col items-center gap-0.5">
            <div className="w-14 h-14 rounded-xl bg-white/80 border border-white/60 flex items-center justify-center shrink-0">
              <WeatherIconComponent descriptor={descriptor} />
            </div>
            <span className="text-xs font-semibold text-slate-500 text-center leading-tight max-w-[56px]">
              {descriptor.label}
            </span>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-400 font-semibold uppercase flex items-center justify-end gap-1">
              <Navigation className="w-3 h-3" />
              Piste
            </div>
            <div className="text-3xl font-black text-green-700 font-tabular leading-none">
              {point.runway}
            </div>
          </div>
        </div>

        {/* Wind range */}
        <div className="rounded-lg bg-white/80 border border-white/60 px-2 py-1.5 text-xs">
          <div className="text-slate-400 font-semibold uppercase flex items-center gap-1 mb-0.5" style={{ fontSize: '0.6rem' }}>
            <Wind className="w-3 h-3" />
            Vent (plage)
          </div>
          <div className="font-bold text-blue-700 font-tabular">{point.windRange}</div>
        </div>

        {/* Min ceiling + min vis */}
        <div className="grid grid-cols-2 gap-1.5 text-xs">
          <div className="rounded-lg bg-white/80 border border-white/60 px-2 py-1.5">
            <div className="text-slate-400 font-semibold uppercase flex items-center gap-1 mb-0.5" style={{ fontSize: '0.6rem' }}>
              <Eye className="w-3 h-3" />
              Vis. min
            </div>
            <div className="font-bold text-slate-700 font-tabular">{point.minVisibility ?? '--'}</div>
          </div>
          <div className="rounded-lg bg-white/80 border border-white/60 px-2 py-1.5">
            <div className="text-slate-400 font-semibold uppercase flex items-center gap-1 mb-0.5" style={{ fontSize: '0.6rem' }}>
              <Cloud className="w-3 h-3" />
              Plaf. min
            </div>
            <div className="font-bold text-slate-700 font-tabular">
              {(point.minCeilingFt ?? 99999) < 99999 ? `${point.minCeilingFt} ft` : 'Dégagé'}
            </div>
          </div>
        </div>

        {/* Phenomena */}
        {point.phenomena && point.phenomena.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {point.phenomena.slice(0, 3).map((ph) => (
              <span key={ph} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold border border-amber-200">
                <AlertTriangle className="w-3 h-3" />
                {ph}
              </span>
            ))}
          </div>
        )}

        {/* TEMPO/PROB indicator */}
        {point.hasTempoOrProb && (
          <div className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
            ⚠ Périodes TEMPO ou PROB prévues
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-slate-400 font-semibold pt-1 border-t border-white/60">
          <span className={point.source === 'TAF' ? 'text-blue-600' : 'text-amber-600'}>
            {point.source}
          </span>
          <span className="truncate max-w-[80px] text-right">
            {point.phenomena && point.phenomena.length > 0 ? point.phenomena[0] : 'RAS'}
          </span>
        </div>
      </article>
    );
  }

  // Standard hourly card — build conditions for icon mapper
  // Use TEMPO/PROB weather for icon priority when active
  const activeWeatherDecoded = point.tempoActive
    ? (point.tempoWeather && point.tempoWeather !== 'RAS' ? point.tempoWeather : point.weather)
    : point.probActive
    ? (point.tempoWeather && point.tempoWeather !== 'RAS' ? point.tempoWeather : point.weather)
    : point.weather;

  const conditions: WeatherConditions = {
    weatherDecoded: activeWeatherDecoded,
    ceilingFt: point.ceilingFt,
    visibility: point.visibility,
    category: point.category,
    tempoActive: point.tempoActive,
    tempoWeatherDecoded: point.tempoWeather,
    tempoCeilingFt: point.tempoCeilingFt,
    tempoCeilingLayer: point.tempoCeilingLayer,
    tempoVisibility: point.tempoVisibility,
    probActive: point.probActive,
  };

  // For TAF-based cards, also pass the raw ceiling layer from parsed data
  const ceilingStr = point.ceiling ?? '';
  if (ceilingStr.startsWith('Dégagé') || ceilingStr === 'Dégagé') {
    conditions.ceilingLayer = 'CLR';
  } else if (ceilingStr.startsWith('Quelques')) {
    conditions.ceilingLayer = 'FEW';
  } else if (ceilingStr.startsWith('Épars')) {
    conditions.ceilingLayer = 'SCT';
  } else if (ceilingStr.startsWith('Fragmenté')) {
    conditions.ceilingLayer = 'BKN';
  } else if (ceilingStr.startsWith('Couvert') || ceilingStr.startsWith('Plafond vertical')) {
    conditions.ceilingLayer = 'OVC';
  }

  const descriptor = getWeatherIconForConditions(conditions);

  // Determine if we should suppress "OK VFR" — never show it when TEMPO/PROB has significant weather
  const hasSignificantTempoProb = (point.tempoActive || point.probActive) &&
    point.tempoWeather && point.tempoWeather !== 'RAS';

  return (
    <article className={`rounded-2xl border shadow-sm p-3 flex flex-col gap-2.5 ${cardThemeFromDescriptor(descriptor, point.tempoActive)}`}>
      {/* Row 1: label + badges */}
      <div className="flex items-start justify-between gap-1 flex-wrap">
        <div>
          <div className="text-base font-bold text-slate-800 font-tabular leading-none">
            {point.label}
          </div>
          {point.key !== 'now' && (
            <div className="text-xs text-slate-400 mt-0.5">heure locale</div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {point.tempoActive && (
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full border bg-amber-100 text-amber-700 border-amber-300 whitespace-nowrap">
              TEMPO
            </span>
          )}
          {point.probActive && (
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full border bg-orange-100 text-orange-700 border-orange-300 whitespace-nowrap">
              PROB{point.probPercent}
            </span>
          )}
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${categoryClass(point.category)}`}>
            {point.category}
          </span>
        </div>
      </div>

      {/* Row 2: icon + runway */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col items-center gap-0.5">
          <div className="w-14 h-14 rounded-xl bg-white/80 border border-white/60 flex items-center justify-center shrink-0">
            <WeatherIconComponent descriptor={descriptor} />
          </div>
          <span className="text-xs font-semibold text-slate-500 text-center leading-tight max-w-[56px]">
            {descriptor.label}
          </span>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400 font-semibold uppercase flex items-center justify-end gap-1">
            <Navigation className="w-3 h-3" />
            Piste
          </div>
          <div className="text-3xl font-black text-green-700 font-tabular leading-none">
            {point.runway}
          </div>
        </div>
      </div>

      {/* Row 3: vent + rafales */}
      <div className="grid grid-cols-2 gap-1.5 text-xs">
        <div className="rounded-lg bg-white/80 border border-white/60 px-2 py-1.5">
          <div className="text-slate-400 font-semibold uppercase flex items-center gap-1 mb-0.5" style={{ fontSize: '0.6rem' }}>
            <Wind className="w-3 h-3" />
            Vent
          </div>
          <div className="font-bold text-blue-700 font-tabular">
            {point.windDirection}° / {point.windSpeed} kt
          </div>
        </div>
        <div className="rounded-lg bg-white/80 border border-white/60 px-2 py-1.5">
          <div className="text-slate-400 font-semibold uppercase flex items-center gap-1 mb-0.5" style={{ fontSize: '0.6rem' }}>
            <AlertTriangle className="w-3 h-3" />
            Raf.
          </div>
          <div className={`font-bold font-tabular ${point.gustSpeed >= 20 ? 'text-amber-700' : 'text-slate-600'}`}>
            {point.gustSpeed > 0 ? `G${point.gustSpeed} kt` : '—'}
          </div>
        </div>

        {/* Row 4: vis + plafond */}
        <div className="rounded-lg bg-white/80 border border-white/60 px-2 py-1.5">
          <div className="text-slate-400 font-semibold uppercase flex items-center gap-1 mb-0.5" style={{ fontSize: '0.6rem' }}>
            <Eye className="w-3 h-3" />
            Vis.
          </div>
          <div className="font-bold text-slate-700 font-tabular">{point.visibility}</div>
        </div>
        <div className="rounded-lg bg-white/80 border border-white/60 px-2 py-1.5">
          <div className="text-slate-400 font-semibold uppercase flex items-center gap-1 mb-0.5" style={{ fontSize: '0.6rem' }}>
            <Cloud className="w-3 h-3" />
            Plaf.
          </div>
          <div className="font-bold text-slate-700 font-tabular">{point.ceiling}</div>
        </div>
      </div>

      {/* TEMPO overlay — shown when TEMPO is active */}
      {point.tempoActive && (
        <div className="rounded-lg bg-amber-50 border border-amber-300 px-2 py-1.5 text-xs">
          <div className="text-amber-700 font-bold mb-1 uppercase" style={{ fontSize: '0.6rem' }}>
            ⚠ Conditions temporaires possibles
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-amber-800 font-semibold">
            {point.tempoWindDirection !== undefined && (
              <span>Vent : {point.tempoWindDirection === 0 ? 'Variable' : `${point.tempoWindDirection}°`} / {point.tempoWindSpeed} kt</span>
            )}
            {point.tempoGustSpeed !== undefined && point.tempoGustSpeed > 0 && (
              <span>Rafales : G{point.tempoGustSpeed} kt</span>
            )}
            {point.tempoVisibility && (
              <span>Visibilité : {point.tempoVisibility}</span>
            )}
            {point.tempoCeilingFt !== undefined && (
              <span>Plafond : {decodeCloudLayer(point.tempoCeilingLayer ?? 'BKN', point.tempoCeilingFt)}</span>
            )}
            {point.tempoWeather && point.tempoWeather !== 'RAS' && (
              <span className="col-span-2">Météo : {point.tempoWeather}</span>
            )}
          </div>
        </div>
      )}

      {/* PROB overlay — shown when PROB is active */}
      {point.probActive && point.tempoWeather && point.tempoWeather !== 'RAS' && (
        <div className="rounded-lg bg-orange-50 border border-orange-300 px-2 py-1.5 text-xs">
          <div className="text-orange-700 font-bold mb-1 uppercase" style={{ fontSize: '0.6rem' }}>
            ⚡ Risque {point.probPercent ? `d'environ ${point.probPercent} %` : 'probable'}
          </div>
          <div className="text-orange-800 font-semibold">
            {point.tempoWeather}
          </div>
          {point.tempoGustSpeed !== undefined && point.tempoGustSpeed > 0 && (
            <div className="text-orange-800 font-semibold">Rafales : G{point.tempoGustSpeed} kt</div>
          )}
          {point.tempoVisibility && (
            <div className="text-orange-800 font-semibold">Visibilité : {point.tempoVisibility}</div>
          )}
        </div>
      )}

      {/* Row 5: enriched data — only on "Actuel" card */}
      {point.key === 'now' && (
        <div className="grid grid-cols-4 gap-1 text-xs">
          <div className="rounded-lg bg-white/80 border border-white px-2 py-1">
            <div className="text-slate-400 font-semibold uppercase" style={{ fontSize: '0.52rem' }}>Temp</div>
            <div className="font-bold text-slate-700 font-tabular">
              {point.temperature !== undefined ? `${point.temperature}°C` : '--'}
            </div>
          </div>
          <div className="rounded-lg bg-white/80 border border-white px-2 py-1">
            <div className="text-slate-400 font-semibold uppercase" style={{ fontSize: '0.52rem' }}>Rosée</div>
            <div className="font-bold text-slate-700 font-tabular">
              {point.dewpoint !== undefined ? `${point.dewpoint}°C` : '--'}
            </div>
          </div>
          <div className="rounded-lg bg-white/80 border border-white px-2 py-1">
            <div className="text-slate-400 font-semibold uppercase" style={{ fontSize: '0.52rem' }}>QNH</div>
            <div className="font-bold text-slate-700 font-tabular">
              {point.pressure ?? '--'}
            </div>
          </div>
          <div className="rounded-lg bg-white/80 border border-white px-2 py-1">
            <div className="text-slate-400 font-semibold uppercase" style={{ fontSize: '0.52rem' }}>Hum.</div>
            <div className="font-bold text-slate-700 font-tabular">
              {point.humidity !== undefined ? `${point.humidity}%` : '--'}
            </div>
          </div>
        </div>
      )}

      {/* Row 6: alerts */}
      <div className="flex flex-wrap gap-1 min-h-[22px]">
        {point.alerts.length > 0 ? (
          point.alerts.slice(0, 4).map((alert) => (
            <span
              key={alert}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-bold border ${
                /Orage|⛈|⚡/.test(alert) ? 'bg-purple-100 text-purple-800 border-purple-300' : /Verglas|🧊/.test(alert) ?'bg-red-100 text-red-800 border-red-300': /Brume|Brouillard|🌫/.test(alert) ?'bg-slate-100 text-slate-700 border-slate-300': /Averses|Pluie|🌧/.test(alert) ?'bg-blue-100 text-blue-800 border-blue-300': /TEMPO/.test(alert) ?'bg-amber-100 text-amber-800 border-amber-300': /PROB/.test(alert) ?'bg-orange-100 text-orange-800 border-orange-300': 'bg-amber-100 text-amber-800 border-amber-200'
              }`}
            >
              {!/⛈|⚡|🧊|🌫|🌧|💨/.test(alert) && <AlertTriangle className="w-3 h-3" />}
              {alert}
            </span>
          ))
        ) : (
          // Only show "OK VFR" if no TEMPO/PROB with significant weather
          !hasSignificantTempoProb ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold border border-green-200">
              ✓ OK VFR
            </span>
          ) : (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200">
              ⚠ À surveiller
            </span>
          )
        )}
      </div>

      {/* Footer: source + wx */}
      <div className="flex items-center justify-between text-xs text-slate-400 font-semibold pt-1 border-t border-white/60">
        <span className={
          point.source === 'METAR' ? 'text-green-600' :
          point.source === 'TAF'   ? 'text-blue-600'  : 'text-amber-600'
        }>
          {point.source}
        </span>
        <span className="truncate max-w-[80px] text-right">{point.weather}</span>
      </div>
    </article>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function ForecastTimeline({ weatherData }: ForecastTimelineProps) {
  const forecast = buildForecast(weatherData);
  const hasTaf = Boolean(weatherData.taf?.raw);

  return (
    <section className="mb-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="text-base font-bold text-foreground">Prévision VFR</h3>
          <p className="text-xs text-muted-foreground">
            Lecture horaire du TAF par heure locale. Confirmer avec les sources officielles.
          </p>
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded-full border ${
          hasTaf
            ? 'bg-blue-50 text-blue-700 border-blue-200' :'bg-amber-50 text-amber-700 border-amber-200'
        }`}>
          {hasTaf ? 'TAF analysé' : 'TAF indisponible'}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {forecast.map((point) => (
          <ForecastCard key={point.key} point={point} />
        ))}
      </div>

      <div className="mt-2 text-xs text-slate-400">
        Lecture simplifiée du TAF. Confirmer avec les sources officielles.
      </div>
    </section>
  );
}
